import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    CircularProgress,
    TextField,
    Autocomplete
} from '@mui/material';
import axios, { CancelTokenSource } from 'axios';
import { User } from '../user/objects/user';
import { Circuit } from './objects/circuit';
import { AppState } from '../../reducers';

type Props = {
    value: User | null,
    onChange: (user: User | null) => void,
    circuit: Circuit | null
}

let request_timeout: NodeJS.Timeout;
let cancelToken: CancelTokenSource;

export function CircuitUserTO(props: Props): JSX.Element {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<User[]>([]);
    const [open, setOpen] = useState<boolean>(false);
    const client_id = useSelector((state: AppState) => state.header.client_id);

    const onSearchChange = (event: React.SyntheticEvent<Element, Event>, value: string) => {
        setSearch(value);
    };

    const runRequest = () => {
        setLoading(true);
        if (typeof cancelToken != typeof undefined) {
            cancelToken.cancel("Operation canceled due to new request.");
        }
        cancelToken = axios.CancelToken.source();
        axios({
            method: 'GET',
            url: `${API_HREF}client/${client_id}/user/`,
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Accept-Language': 'fr'
            },
            cancelToken: cancelToken.token,
            params: {
                search,
                limit: 50
            }
        }).then((response) => {
            setOptions(response.data.results);
            setLoading(false);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.log('error : ', error);
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        if(open) {
            clearTimeout(request_timeout);
            request_timeout = setTimeout(runRequest, 500);
        } else {
            setOptions([]);
        }
    }, [search, open]);

    // recreate input
    useEffect(() => {
        if (props.circuit?.trip?.user_to) {
            props.onChange(props.circuit.trip.user_to);
        }
    }, [props.circuit]);

    return (
        <Autocomplete
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.username ?? `${option.first_name} ${option.last_name}`}
            options={ options }
            onOpen={() => {
                setOpen(true);
                setSearch('');
            }}
            loading={ loading }
            value={ props.value }
            filterOptions={(options) => options}
            onInputChange={onSearchChange}
            onChange={(event, value) => {
                props.onChange(value);
            }}
            noOptionsText={ t('list.autocomplete_no_options_text') }
            loadingText={ t('list.autocomplete_loading_text') }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={ t('shared.circuit-designer') }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                { loading ? <CircularProgress color={ 'inherit' } size={ 20 }/> : null }
                                { params.InputProps.endAdornment }
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}